import React, { useState, useEffect, useRef, useCallback } from "react";
import PropTypes from "prop-types";
import { InputTextarea } from "primereact/inputtextarea";
import SelectField from "../../components/inputs/select";
import Button from "../../components/inputs/button";
import Toast from "../../components/toast";
import Loader from "../../components/misc/loader";
import { getTags } from "../../services/tag";
import { generateAudiencesV2 } from "../../services/agenticV2";
import "./index.scss";

const ACCEPTED_EXTENSIONS = ".pdf,.docx,.pptx,.xlsx,.txt,.md";

const OPENAI_MODELS = [
	{ label: "GPT-5", value: "gpt-5" },
	{ label: "GPT-5 Mini", value: "gpt-5-mini" },
	{ label: "GPT-5 Nano", value: "gpt-5-nano" },
	{ label: "GPT-4.1", value: "gpt-4.1" },
	{ label: "GPT-4.1 Mini", value: "gpt-4.1-mini" },
	{ label: "GPT-4o", value: "gpt-4o" },
	{ label: "GPT-4o Mini", value: "gpt-4o-mini" },
];

const BEDROCK_MODELS = [
	{ label: "Claude Opus 4.6", value: "us.anthropic.claude-opus-4-6-v1" },
	{ label: "Claude Opus 4.5", value: "us.anthropic.claude-opus-4-5-20251101-v1:0" },
	{ label: "Claude Sonnet 4.5", value: "us.anthropic.claude-sonnet-4-5-20250929-v1:0" },
	{ label: "Claude Haiku 4.5", value: "us.anthropic.claude-haiku-4-5-20251001-v1:0" },
	{ label: "Claude Opus 4.1", value: "us.anthropic.claude-opus-4-1-20250805-v1:0" },
	{ label: "Claude Sonnet 4", value: "us.anthropic.claude-sonnet-4-20250514-v1:0" },
	{ label: "Claude 3.7 Sonnet", value: "us.anthropic.claude-3-7-sonnet-20250219-v1:0" },
	{ label: "Claude 3.5 Sonnet v2", value: "us.anthropic.claude-3-5-sonnet-20241022-v2:0" },
	{ label: "Claude 3.5 Sonnet v1", value: "us.anthropic.claude-3-5-sonnet-20240620-v1:0" },
];

const MODEL_OPTIONS = [
	{ label: "OpenAI", options: OPENAI_MODELS },
	{ label: "Anthropic (Bedrock)", options: BEDROCK_MODELS },
];

const STEP_MODEL_OPTIONS = [
	{ label: "OpenAI", options: OPENAI_MODELS },
	{ label: "Anthropic (Bedrock)", options: BEDROCK_MODELS },
];

const STEP_DEFAULTS = {
	extract_strategy: { label: "Claude Haiku 4.5", value: "us.anthropic.claude-haiku-4-5-20251001-v1:0" },
	plan_audiences: { label: "Claude Sonnet 4.5", value: "us.anthropic.claude-sonnet-4-5-20250929-v1:0" },
	build_criteria: { label: "Claude Sonnet 4.5", value: "us.anthropic.claude-sonnet-4-5-20250929-v1:0" },
	assemble_audience: { label: "Claude Sonnet 4.5", value: "us.anthropic.claude-sonnet-4-5-20250929-v1:0" },
};

const TARGETING_MODE_OPTIONS = [
	{ label: "full", value: "full" },
	{ label: "without_third_party", value: "without_third_party" },
	{ label: "contextual_only", value: "contextual_only" },
];

function CollapsibleJSON({ title, data, expandedSections, toggleSection, sectionKey }) {
	const isOpen = expandedSections[sectionKey] || false;
	return (
		<div className="debug-collapsible">
			<button
				className="debug-collapsible-header"
				onClick={() => toggleSection(sectionKey)}
			>
				<span className={`chevron ${isOpen ? "open" : ""}`}>&#9654;</span>
				{title}
			</button>
			{isOpen && (
				<pre className="debug-json-block">{JSON.stringify(data, null, 2)}</pre>
			)}
		</div>
	);
}

CollapsibleJSON.propTypes = {
	title: PropTypes.string.isRequired,
	data: PropTypes.any,
	expandedSections: PropTypes.object.isRequired,
	toggleSection: PropTypes.func.isRequired,
	sectionKey: PropTypes.string.isRequired,
};

// Renders the audience_size field returned by the agentic-builder API.
// Sketch-based size estimate from the embedded WASM Theta sizer; only silos
// with built sketches return numbers. Sketches are keyed on SID
// (per-pageview ID), so the estimate is qualifying-pageview count over the
// 30-day lookback (V2's TOTAL semantic), not distinct users (V2's UNIQUES).
// See agentic-builder-v2/src/services/sketch-sizer/README.md.
function AudienceSizeBadge({ size }) {
	if (!size) return null;

	if (size.status === "ok") {
		const estimate = Number(size.estimate).toLocaleString();
		const lower = Number(size.lower2sd).toLocaleString();
		const upper = Number(size.upper2sd).toLocaleString();
		return (
			<div className="size-badge size-ok">
				<span className="size-label">Qualifying page views (30d)</span>
				<span className="size-estimate">{estimate}</span>
				<span className="size-bounds">
					({lower} – {upper}, ±2σ)
				</span>
				<span className="size-duration">{size.duration_ms}ms</span>
			</div>
		);
	}

	const headlines = {
		unsupported: "Size unavailable — rule shape outside V1 fast-path",
		unsupported_silo: "Size unavailable — no sketches built for this silo",
		error: "Size errored",
	};
	return (
		<div className={`size-badge size-${size.status}`}>
			<span className="size-label">{headlines[size.status] || `Size: ${size.status}`}</span>
			{size.message && <span className="size-message">{size.message}</span>}
		</div>
	);
}

AudienceSizeBadge.propTypes = {
	size: PropTypes.shape({
		status: PropTypes.oneOf(["ok", "unsupported", "unsupported_silo", "error"]).isRequired,
		estimate: PropTypes.number,
		lower2sd: PropTypes.number,
		upper2sd: PropTypes.number,
		message: PropTypes.string,
		duration_ms: PropTypes.number,
	}),
};

function DebugAudienceGenerate() {
	// Form state
	const [siloId, setSiloId] = useState(null);
	const [siloOptions, setSiloOptions] = useState([]);
	const [targetingText, setTargetingText] = useState("");
	const [rfpFile, setRfpFile] = useState(null);
	const [model, setModel] = useState(null);
	const [stepModels, setStepModels] = useState({
		extract_strategy: null,
		plan_audiences: null,
		build_criteria: null,
		assemble_audience: null,
	});
	const [temperature, setTemperature] = useState(0.7);
	const [targetingMode, setTargetingMode] = useState({ label: "full", value: "full" });

	// Request lifecycle
	const [loading, setLoading] = useState(false);
	const [elapsedMs, setElapsedMs] = useState(0);
	const [error, setError] = useState(null);

	// Response
	const [response, setResponse] = useState(null);

	// UI
	const [toast, setToast] = useState(null);
	const [expandedSections, setExpandedSections] = useState({});
	const [dragOver, setDragOver] = useState(false);

	const fileInputRef = useRef(null);
	const timerRef = useRef(null);

	// Load silo options
	useEffect(() => {
		getTags().then((res) => {
			const options = res.data
				.map((t) => ({ label: t.name, value: t.tagId }))
				.sort((a, b) => a.label.localeCompare(b.label));
			setSiloOptions(options);
			const active = localStorage.getItem("activeSilo");
			if (active) {
				const found = options.find((o) => o.value == active);
				if (found) setSiloId(found);
			}
		});
	}, []);

	// Elapsed timer
	useEffect(() => {
		if (loading) {
			const start = Date.now();
			timerRef.current = setInterval(() => {
				setElapsedMs(Date.now() - start);
			}, 100);
		} else {
			clearInterval(timerRef.current);
		}
		return () => clearInterval(timerRef.current);
	}, [loading]);

	const toggleSection = useCallback((key) => {
		setExpandedSections((prev) => ({ ...prev, [key]: !prev[key] }));
	}, []);

	const handleFileSelect = (file) => {
		if (!file) return;
		const ext = "." + file.name.split(".").pop().toLowerCase();
		if (!ACCEPTED_EXTENSIONS.includes(ext)) {
			setToast({ status: "Invalid", title: "Invalid File", message: `Unsupported file type: ${ext}` });
			return;
		}
		setRfpFile(file);
	};

	const handleDrop = (e) => {
		e.preventDefault();
		setDragOver(false);
		if (e.dataTransfer.files.length > 0) {
			handleFileSelect(e.dataTransfer.files[0]);
		}
	};

	const handleSubmit = async () => {
		if (!siloId) {
			setToast({ status: "Invalid", title: "Validation", message: "Please select a silo." });
			return;
		}
		if (!targetingText.trim() && !rfpFile) {
			setToast({
				status: "Invalid",
				title: "Validation",
				message: "Provide targeting text or an RFP file.",
			});
			return;
		}

		setLoading(true);
		setError(null);
		setResponse(null);
		setElapsedMs(0);
		setExpandedSections({});

		try {
			// Build per-step overrides (only include steps that have a selection)
			const stepOverrides = {};
			for (const [key, val] of Object.entries(stepModels)) {
				if (val?.value) stepOverrides[key] = val.value;
			}

			const result = await generateAudiencesV2({
				siloId: siloId.value,
				targetingText: targetingText.trim() || undefined,
				rfpFile: rfpFile || undefined,
				model: model?.value || undefined,
				stepModels: Object.keys(stepOverrides).length > 0 ? stepOverrides : undefined,
				temperature: parseFloat(temperature),
				targetingMode: targetingMode.value,
				includeDebug: true,
			});
			setResponse(result);
		} catch (err) {
			setError(err);
		} finally {
			setLoading(false);
		}
	};

	const audiences = response?.audiences || [];
	const debugInfo = response?.debug_info || {};
	const timing = debugInfo.timing || {};
	const requestInfo = debugInfo.request_info || {};

	return (
		<div id="debug-audience-generate">
			<h2>Debug: Audience Generate (v2)</h2>

			{toast && (
				<Toast
					status={toast.status}
					title={toast.title}
					message={toast.message}
					callback={() => setToast(null)}
					autoHide={toast.status !== "Invalid"}
				/>
			)}

			<div className="debug-layout">
				{/* Left column: Input form */}
				<div className="input-panel">
					<div className="form-section">
						<label>Silo</label>
						<SelectField
							name="silo"
							options={siloOptions}
							selected={siloId}
							onSubmit={setSiloId}
							required={true}
							disabled={false}
							search={true}
							placeholder="Select silo..."
						/>
					</div>

					<div className="form-section">
						<label>Targeting Text</label>
						<InputTextarea
							value={targetingText}
							onChange={(e) => setTargetingText(e.target.value)}
							rows={8}
							placeholder="Paste targeting text or RFP content..."
							style={{ width: "100%" }}
						/>
					</div>

					<div className="form-section">
						<label>RFP File</label>
						{rfpFile ? (
							<div className="file-selected">
								<span className="file-name">{rfpFile.name}</span>
								<button
									className="file-remove"
									onClick={() => {
										setRfpFile(null);
										if (fileInputRef.current) fileInputRef.current.value = "";
									}}
								>
									Remove
								</button>
							</div>
						) : (
							<div
								className={`file-upload-area ${dragOver ? "drag-over" : ""}`}
								onClick={() => fileInputRef.current?.click()}
								onDragOver={(e) => {
									e.preventDefault();
									setDragOver(true);
								}}
								onDragLeave={() => setDragOver(false)}
								onDrop={handleDrop}
							>
								<div>Drop a file here or click to browse</div>
								<div className="file-types">PDF, DOCX, PPTX, XLSX, TXT, MD</div>
								<input
									ref={fileInputRef}
									type="file"
									accept={ACCEPTED_EXTENSIONS}
									onChange={(e) => handleFileSelect(e.target.files[0])}
								/>
							</div>
						)}
					</div>

					<div className="form-section">
						<label>Global Model Override</label>
						<SelectField
							name="model"
							options={MODEL_OPTIONS}
							selected={model}
							onSubmit={setModel}
							required={false}
							disabled={false}
							search={true}
							placeholder="Use per-step defaults"
						/>
						{model && (
							<button
								className="file-remove"
								style={{ marginTop: "0.25rem" }}
								onClick={() => setModel(null)}
							>
								Clear
							</button>
						)}
					</div>

					<div className="form-section">
						<label>Per-Step Models</label>
						<div className="step-models-grid">
							{[
								{ key: "extract_strategy", label: "Step 1 (Extract)" },
								{ key: "plan_audiences", label: "Step 2 (Plan)" },
								{ key: "build_criteria", label: "Step 3 (Build)" },
								{ key: "assemble_audience", label: "Step 4 (Assemble)" },
							].map(({ key, label }) => (
								<div key={key} className="step-model-row">
									<span className="step-model-label">{label}</span>
									<span className="step-model-default">
										Default: {STEP_DEFAULTS[key].label}
									</span>
									<div className="step-model-select">
										<SelectField
											name={`step-${key}`}
											options={STEP_MODEL_OPTIONS}
											selected={stepModels[key]}
											onSubmit={(val) =>
												setStepModels((prev) => ({
													...prev,
													[key]: val?.value ? val : null,
												}))
											}
											required={false}
											disabled={false}
											search={true}
											placeholder={STEP_DEFAULTS[key].label}
										/>
										{stepModels[key] && (
											<button
												className="file-remove"
												onClick={() =>
													setStepModels((prev) => ({
														...prev,
														[key]: null,
													}))
												}
											>
												Clear
											</button>
										)}
									</div>
								</div>
							))}
						</div>
					</div>

					<div className="form-section">
						<label>Temperature</label>
						<input
							type="number"
							value={temperature}
							onChange={(e) => setTemperature(e.target.value)}
							min={0}
							max={2}
							step={0.1}
						/>
					</div>

					<div className="form-section">
						<label>Targeting Mode</label>
						<SelectField
							name="targetingMode"
							options={TARGETING_MODE_OPTIONS}
							selected={targetingMode}
							onSubmit={setTargetingMode}
							required={true}
							disabled={false}
							search={false}
						/>
					</div>

					<Button
						text="Generate"
						type="button"
						style="dark"
						disabled={loading}
						loaded={loading}
						onClick={handleSubmit}
					/>
				</div>

				{/* Right column: Results */}
				<div className="results-panel">
					{loading && (
						<div className="loading-state">
							<Loader size="md" />
							<span className="elapsed">
								{(elapsedMs / 1000).toFixed(1)}s elapsed
							</span>
						</div>
					)}

					{error && !loading && (
						<div className="error-display">
							{error.message || "An unknown error occurred."}
						</div>
					)}

					{response && !loading && (
						<>
							{/* Primary Results */}
							{response.requirements_summary && (
								<div className="result-section">
									<h3>Requirements Summary</h3>
									<div className="text-block">
										{response.requirements_summary}
									</div>
								</div>
							)}

							{response.strategy_analysis && (
								<div className="result-section">
									<h3>Strategy Analysis</h3>
									<div className="text-block">
										{response.strategy_analysis}
									</div>
								</div>
							)}

							{audiences.length > 0 && (
								<div className="result-section">
									<h3>Audiences ({audiences.length})</h3>
									{audiences.map((aud, i) => (
										<div key={i} className="audience-result">
											<div className="audience-header">
												<span className="audience-name">
													{aud.name || `Audience ${i + 1}`}
												</span>
												{aud.theme && (
													<span className="theme-badge">{aud.theme}</span>
												)}
											</div>
											{aud.differentiation && (
												<div className="differentiation">
													{aud.differentiation}
												</div>
											)}
											<AudienceSizeBadge size={aud.audience_size} />
											<CollapsibleJSON
												title="composite_conditions"
												data={aud.composite_conditions}
												expandedSections={expandedSections}
												toggleSection={toggleSection}
												sectionKey={`aud-${i}-conditions`}
											/>
											{aud.validation_warnings?.length > 0 && (
												<ul className="validation-warnings">
													{aud.validation_warnings.map((w, j) => (
														<li key={j}>{w}</li>
													))}
												</ul>
											)}
										</div>
									))}
								</div>
							)}

							{/* Debug Panels */}
							<div className="result-section">
								<h3>Debug Info</h3>

								{/* Timing */}
								{Object.keys(timing).length > 0 && (
									<>
										<h4 style={{ fontSize: "0.9rem", margin: "0.75rem 0 0.35rem" }}>
											Timing
										</h4>
										<table className="timing-table">
											<thead>
												<tr>
													<th>Step</th>
													<th>Duration (ms)</th>
												</tr>
											</thead>
											<tbody>
												{Object.entries(timing).map(([step, ms]) => (
													<tr key={step}>
														<td>{step}</td>
														<td>{typeof ms === "number" ? ms.toLocaleString() : ms}</td>
													</tr>
												))}
											</tbody>
										</table>
									</>
								)}

								{/* Request Info */}
								{Object.keys(requestInfo).length > 0 && (
									<>
										<h4 style={{ fontSize: "0.9rem", margin: "0.75rem 0 0.35rem" }}>
											Request Info
										</h4>
										<dl className="request-info">
											{Object.entries(requestInfo).map(([key, val]) => (
												<React.Fragment key={key}>
													<dt>{key}</dt>
													<dd>
													{typeof val === "object" && val !== null
														? JSON.stringify(val, null, 2)
														: String(val)}
												</dd>
												</React.Fragment>
											))}
										</dl>
									</>
								)}

								{/* Step 1 — Extraction */}
								{debugInfo.step1_extraction_response && (
									<CollapsibleJSON
										title="Step 1 — Extraction"
										data={debugInfo.step1_extraction_response}
										expandedSections={expandedSections}
										toggleSection={toggleSection}
										sectionKey="step1"
									/>
								)}

								{/* Step 2 — Audience Plan */}
								{debugInfo.step2_audience_plan && (
									<CollapsibleJSON
										title="Step 2 — Audience Plan"
										data={debugInfo.step2_audience_plan}
										expandedSections={expandedSections}
										toggleSection={toggleSection}
										sectionKey="step2"
									/>
								)}

								{/* Step 3 — Criteria Data */}
								{debugInfo.step3_criteria_data?.map((item, i) => (
									<CollapsibleJSON
										key={`step3-${i}`}
										title={`Step 3 — Criteria Data [${i}]`}
										data={item}
										expandedSections={expandedSections}
										toggleSection={toggleSection}
										sectionKey={`step3-${i}`}
									/>
								))}

								{/* Step 4 — Assembly */}
								{debugInfo.step4_assembly_responses?.map((item, i) => (
									<CollapsibleJSON
										key={`step4-${i}`}
										title={`Step 4 — Assembly [${i}]`}
										data={item}
										expandedSections={expandedSections}
										toggleSection={toggleSection}
										sectionKey={`step4-${i}`}
									/>
								))}
							</div>
						</>
					)}
				</div>
			</div>
		</div>
	);
}

export default DebugAudienceGenerate;
